import { LoanApplication, FraudDetectionResult, FraudIndicator, RiskFactor } from '../types';

export interface FraudDetectionConfig {
  documentVerificationThreshold: number;
  documentVerificationEnabled: boolean;
  behavioralAnalysisEnabled: boolean;
  identityVerificationEnabled: boolean;
  socialMediaAnalysisEnabled: boolean;
  deviceFingerprintingEnabled: boolean;
  locationAnalysisEnabled: boolean;
  riskWeights: {
    document: number;
    behavioral: number;
    financial: number;
    identity: number;
    social: number;
  };
}

const defaultConfig: FraudDetectionConfig = {
  documentVerificationThreshold: 0.7,
  documentVerificationEnabled: true,
  behavioralAnalysisEnabled: true,
  identityVerificationEnabled: true,
  socialMediaAnalysisEnabled: true,
  deviceFingerprintingEnabled: true,
  locationAnalysisEnabled: true,
  riskWeights: {
    document: 0.25,
    behavioral: 0.20,
    financial: 0.25,
    identity: 0.20,
    social: 0.10,
  },
};

export class FraudDetectionService {
  private config: FraudDetectionConfig;

  constructor(config?: Partial<FraudDetectionConfig>) {
    this.config = { ...defaultConfig, ...config };
  }

  /**
   * Comprehensive fraud detection analysis
   */
  public async detectFraud(application: LoanApplication): Promise<FraudDetectionResult> {
    const fraudIndicators: FraudIndicator[] = [];
    let totalFraudScore = 0;

    // Document verification analysis
    if (this.config.documentVerificationEnabled) {
      const documentIndicators = this.analyzeDocumentRisk(application);
      fraudIndicators.push(...documentIndicators);
      totalFraudScore += this.calculateDocumentRiskScore(documentIndicators);
    }

    // Behavioral analysis
    if (this.config.behavioralAnalysisEnabled) {
      const behavioralIndicators = this.analyzeBehavioralRisk(application);
      fraudIndicators.push(...behavioralIndicators);
      totalFraudScore += this.calculateBehavioralRiskScore(behavioralIndicators);
    }

    // Financial analysis
    const financialIndicators = this.analyzeFinancialRisk(application);
    fraudIndicators.push(...financialIndicators);
    totalFraudScore += this.calculateFinancialRiskScore(financialIndicators);

    // Identity verification
    if (this.config.identityVerificationEnabled) {
      const identityIndicators = this.analyzeIdentityRisk(application);
      fraudIndicators.push(...identityIndicators);
      totalFraudScore += this.calculateIdentityRiskScore(identityIndicators);
    }

    // Social media analysis
    if (this.config.socialMediaAnalysisEnabled) {
      const socialIndicators = this.analyzeSocialRisk(application);
      fraudIndicators.push(...socialIndicators);
      totalFraudScore += this.calculateSocialRiskScore(socialIndicators);
    }

    // Normalize fraud score to 0-100 range
    const normalizedFraudScore = Math.min(100, Math.max(0, totalFraudScore));
    
    // Determine risk level and recommended action
    const riskLevel = this.determineRiskLevel(normalizedFraudScore);
    const recommendedAction = this.determineRecommendedAction(normalizedFraudScore, fraudIndicators);
    const confidence = this.calculateConfidence(fraudIndicators);

    return {
      isFraudulent: normalizedFraudScore > 70,
      fraudScore: normalizedFraudScore,
      fraudIndicators,
      riskLevel,
      recommendedAction,
      confidence,
    };
  }

  /**
   * Analyze document-related fraud risks
   */
  private analyzeDocumentRisk(application: LoanApplication): FraudIndicator[] {
    const indicators: FraudIndicator[] = [];

    // Check for document inconsistencies
    if (application.kycData?.documents) {
      const documents = application.kycData.documents;
      
      // Check if all required documents are present
      const requiredDocs = ['identityProof', 'addressProof', 'incomeProof'];
      const missingDocs = requiredDocs.filter(docType => {
        const docArray = documents[docType as keyof typeof documents];
        return !docArray || docArray.length === 0;
      });

      if (missingDocs.length > 0) {
        indicators.push({
          type: 'document',
          description: `Missing required documents: ${missingDocs.join(', ')}`,
          severity: 'high',
          evidence: `Required: ${requiredDocs.join(', ')}, Missing: ${missingDocs.join(', ')}`,
          weight: 15,
        });
      }

      // Check document verification status (only if documents have verification status)
      // For now, we'll skip this check since the current type definition doesn't support it
      // In a real implementation, this would check KYCDocument verification status
    }

    // Check for suspicious document patterns
    if (application.kycData?.auditTrail) {
      const recentUploads = application.kycData.auditTrail
        .filter(entry => entry.action === 'upload' && entry.status === 'success')
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      if (recentUploads.length > 5) {
        indicators.push({
          type: 'document',
          description: 'Unusually high number of document uploads',
          severity: 'medium',
          evidence: `${recentUploads.length} uploads in recent activity`,
          weight: 8,
        });
      }
    }

    return indicators;
  }

  /**
   * Analyze behavioral patterns for fraud detection
   */
  private analyzeBehavioralRisk(application: LoanApplication): FraudIndicator[] {
    const indicators: FraudIndicator[] = [];

    // Check application timing patterns
    const submissionTime = new Date(application.submissionDate);
    const hour = submissionTime.getHours();
    
    // Suspicious: Applications submitted during unusual hours (2 AM - 6 AM)
    if (hour >= 2 && hour <= 6) {
      indicators.push({
        type: 'behavioral',
        description: 'Application submitted during unusual hours',
        severity: 'medium',
        evidence: `Submitted at ${hour}:00 (suspicious time window)`,
        weight: 5,
      });
    }

    // Check for rapid application submissions
    if (application.kycData?.auditTrail) {
      const applicationActions = application.kycData.auditTrail
        .filter(entry => entry.action === 'submit' || entry.action === 'create')
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

      if (applicationActions.length > 1) {
        const timeDiff = new Date(applicationActions[1].timestamp).getTime() - 
                        new Date(applicationActions[0].timestamp).getTime();
        const minutesDiff = timeDiff / (1000 * 60);

        if (minutesDiff < 5) {
          indicators.push({
            type: 'behavioral',
            description: 'Application completed too quickly (suspicious)',
            severity: 'medium',
            evidence: `Completed in ${minutesDiff.toFixed(1)} minutes`,
            weight: 8,
          });
        }
      }
    }

    return indicators;
  }

  /**
   * Analyze financial patterns for fraud detection
   */
  private analyzeFinancialRisk(application: LoanApplication): FraudIndicator[] {
    const indicators: FraudIndicator[] = [];

    // Check income vs loan amount ratio
    const incomeToLoanRatio = application.requestedAmount / application.monthlyIncome;
    
    if (incomeToLoanRatio > 24) { // More than 2 years of income
      indicators.push({
        type: 'financial',
        description: 'Loan amount exceeds reasonable income multiple',
        severity: 'high',
        evidence: `Loan: ${application.requestedAmount}, Income: ${application.monthlyIncome}, Ratio: ${incomeToLoanRatio.toFixed(1)}`,
        weight: 20,
      });
    }

    // Check for suspicious income patterns
    if (application.monthlyIncome < 500000) { // Very low income
      indicators.push({
        type: 'financial',
        description: 'Suspiciously low monthly income',
        severity: 'medium',
        evidence: `Monthly income: ${application.monthlyIncome} (below threshold)`,
        weight: 12,
      });
    }

    // Check existing debt burden
    if (application.existingLoans > 3) {
      indicators.push({
        type: 'financial',
        description: 'High existing debt burden',
        severity: 'high',
        evidence: `Existing loans: ${application.existingLoans}`,
        weight: 18,
      });
    }

    // Check employment duration vs income
    if (application.employmentDuration < 6 && application.monthlyIncome > 2000000) {
      indicators.push({
        type: 'financial',
        description: 'Suspicious income level for employment duration',
        severity: 'medium',
        evidence: `Income: ${application.monthlyIncome}, Employment: ${application.employmentDuration} months`,
        weight: 10,
      });
    }

    return indicators;
  }

  /**
   * Analyze identity-related fraud risks
   */
  private analyzeIdentityRisk(application: LoanApplication): FraudIndicator[] {
    const indicators: FraudIndicator[] = [];

    // Check age vs employment duration
    if (application.age < 22 && application.employmentDuration > 5) {
      indicators.push({
        type: 'identity',
        description: 'Suspicious employment duration for age',
        severity: 'high',
        evidence: `Age: ${application.age}, Employment: ${application.employmentDuration} years`,
        weight: 25,
      });
    }

    // Check for age-related inconsistencies
    if (application.age < 18) {
      indicators.push({
        type: 'identity',
        description: 'Applicant below legal age',
        severity: 'critical',
        evidence: `Age: ${application.age} (below legal minimum)`,
        weight: 100,
      });
    }

    // Check education vs age consistency
    if (application.kycData?.personalInfo?.educationLevel) {
      const education = application.kycData.personalInfo.educationLevel;
      const age = application.age;
      
      if (education === 'postgraduate' && age < 25) {
        indicators.push({
          type: 'identity',
          description: 'Suspicious education level for age',
          severity: 'medium',
          evidence: `Education: ${education}, Age: ${age}`,
          weight: 8,
        });
      }
    }

    return indicators;
  }

  /**
   * Analyze social media and online presence risks
   */
  private analyzeSocialRisk(application: LoanApplication): FraudIndicator[] {
    const indicators: FraudIndicator[] = [];

    // Mock social media analysis (in real implementation, this would call external APIs)
    // For now, we'll simulate based on available data
    
    // Check if email domain is suspicious
    const emailDomain = application.email.split('@')[1];
    const suspiciousDomains = ['tempmail.com', '10minutemail.com', 'guerrillamail.com'];
    
    if (suspiciousDomains.includes(emailDomain)) {
      indicators.push({
        type: 'social',
        description: 'Suspicious email domain detected',
        severity: 'high',
        evidence: `Email domain: ${emailDomain} (temporary email service)`,
        weight: 20,
      });
    }

    // Check phone number patterns
    const phone = application.phone.replace(/\D/g, '');
    if (phone.length < 10 || phone.length > 15) {
      indicators.push({
        type: 'social',
        description: 'Invalid phone number format',
        severity: 'medium',
        evidence: `Phone: ${application.phone} (length: ${phone.length})`,
        weight: 10,
      });
    }

    return indicators;
  }

  /**
   * Calculate risk scores for different categories
   */
  private calculateDocumentRiskScore(indicators: FraudIndicator[]): number {
    return indicators
      .filter(indicator => indicator.type === 'document')
      .reduce((score, indicator) => score + (indicator.weight * this.getSeverityMultiplier(indicator.severity)), 0);
  }

  private calculateBehavioralRiskScore(indicators: FraudIndicator[]): number {
    return indicators
      .filter(indicator => indicator.type === 'behavioral')
      .reduce((score, indicator) => score + (indicator.weight * this.getSeverityMultiplier(indicator.severity)), 0);
  }

  private calculateFinancialRiskScore(indicators: FraudIndicator[]): number {
    return indicators
      .filter(indicator => indicator.type === 'financial')
      .reduce((score, indicator) => score + (indicator.weight * this.getSeverityMultiplier(indicator.severity)), 0);
  }

  private calculateIdentityRiskScore(indicators: FraudIndicator[]): number {
    return indicators
      .filter(indicator => indicator.type === 'identity')
      .reduce((score, indicator) => score + (indicator.weight * this.getSeverityMultiplier(indicator.severity)), 0);
  }

  private calculateSocialRiskScore(indicators: FraudIndicator[]): number {
    return indicators
      .filter(indicator => indicator.type === 'social')
      .reduce((score, indicator) => score + (indicator.weight * this.getSeverityMultiplier(indicator.severity)), 0);
  }

  /**
   * Get severity multiplier for scoring
   */
  private getSeverityMultiplier(severity: string): number {
    switch (severity) {
      case 'low': return 0.5;
      case 'medium': return 1.0;
      case 'high': return 1.5;
      case 'critical': return 2.0;
      default: return 1.0;
    }
  }

  /**
   * Determine overall risk level
   */
  private determineRiskLevel(fraudScore: number): 'low' | 'medium' | 'high' | 'critical' {
    if (fraudScore >= 80) return 'critical';
    if (fraudScore >= 60) return 'high';
    if (fraudScore >= 30) return 'medium';
    return 'low';
  }

  /**
   * Determine recommended action
   */
  private determineRecommendedAction(fraudScore: number, indicators: FraudIndicator[]): 'approve' | 'review' | 'reject' | 'investigate' {
    if (fraudScore >= 80) return 'reject';
    if (fraudScore >= 60) return 'investigate';
    if (fraudScore >= 30) return 'review';
    return 'approve';
  }

  /**
   * Calculate confidence level based on data quality
   */
  private calculateConfidence(indicators: FraudIndicator[]): number {
    // Base confidence on data completeness and quality
    let confidence = 100;
    
    // Reduce confidence for each high-severity indicator
    const highSeverityCount = indicators.filter(i => i.severity === 'high' || i.severity === 'critical').length;
    confidence -= highSeverityCount * 10;
    
    // Ensure confidence stays within bounds
    return Math.max(0, Math.min(100, confidence));
  }

  /**
   * Generate risk factors for the application
   */
  public generateRiskFactors(application: LoanApplication, fraudResult: FraudDetectionResult): RiskFactor[] {
    const riskFactors: RiskFactor[] = [];

    // Add fraud-related risk factors
    if (fraudResult.fraudScore > 30) {
      riskFactors.push({
        factor: 'Fraud Risk',
        impact: fraudResult.riskLevel === 'critical' ? 'high' : 'medium',
        probability: fraudResult.fraudScore / 100,
        mitigation: 'Enhanced verification required',
        category: 'documentary',
      });
    }

    // Add financial risk factors
    const incomeToLoanRatio = application.requestedAmount / application.monthlyIncome;
    if (incomeToLoanRatio > 12) {
      riskFactors.push({
        factor: 'High Debt-to-Income Ratio',
        impact: 'high',
        probability: Math.min(0.9, incomeToLoanRatio / 24),
        mitigation: 'Reduce loan amount or increase income verification',
        category: 'financial',
      });
    }

    // Add employment risk factors
    if (application.employmentDuration < 12) {
      riskFactors.push({
        factor: 'Short Employment Duration',
        impact: 'medium',
        probability: 0.6,
        mitigation: 'Additional employment verification required',
        category: 'financial',
      });
    }

    // Add age-related risk factors
    if (application.age < 25 || application.age > 65) {
      riskFactors.push({
        factor: 'Age-Related Risk',
        impact: 'medium',
        probability: 0.5,
        mitigation: 'Age-appropriate loan terms and conditions',
        category: 'financial',
      });
    }

    return riskFactors;
  }
}

export default FraudDetectionService;
